import { generateLangContent } from '@/lib/helpers/lang-content-helper'
import { EDietType } from '../constants/dietList'

export const DietExcludedAllergen = (json: IDietExcludedAllergen): IDietExcludedAllergenModel => {
	const { name, is_active, updated_at, created_at, ...rest } = json

	return {
		...rest,
		name: LangContentToModel(name),
		isActive: is_active,
		updatedAt: updated_at ? new Date(updated_at) : null,
		createdAt: created_at ? new Date(created_at) : null,
	}
}

export const Diet = (json: IDiet): IDietModel => {
	const fromJSON = (itemJson: IDiet): IDietModel => {
		const {
			name,
			name_short,
			description,
			description_short,
			diet_type,
			active_to_order_from,
			active_to_order_to,
			prices,
			carbs_percent,
			protein_percent,
			fat_percent,
			meal_types,
			media,
			filter_tags,
			marketing_tags,
			allergen_exclusions,
			dietary_tag_exclusions,
			...rest
		} = itemJson

		return {
			name: name ? LangContentToModel(name) : undefined,
			nameShort: name_short ? LangContentToModel(name_short) : undefined,
			description: description ? LangContentToModel(description) : undefined,
			descriptionShort: description_short ? LangContentToModel(description_short) : undefined,
			dietType: diet_type,
			activeToOrderFrom: active_to_order_from ? new Date(active_to_order_from) : null,
			activeToOrderTo: active_to_order_to ? new Date(active_to_order_to) : null,
			prices: prices
				? prices.map(price => ({
						kcal: price.kcal,
						price: price.price || price.price === 0 ? price.price / 100 : null,
					}))
				: [],
			carbsPercent: carbs_percent,
			proteinsPercent: protein_percent,
			fatsPercent: fat_percent,
			mealTypes: meal_types ? meal_types.map(mealType => DietMealType(mealType)) : [],
			media: media ? media.map(m => Media(m)) : [],
			filterTags: filter_tags ? filter_tags.map(f => DietFilterTag(f)) : [],
			marketingTags: marketing_tags ? marketing_tags.map(m => DietMarketingTag(m)) : [],
			allergenExclusions: allergen_exclusions ? allergen_exclusions.map(e => DietExcludedAllergen(e)) : [],
			dietaryTagExclusions: dietary_tag_exclusions ? dietary_tag_exclusions.map(e => DietDietaryTag(e)) : [],
			...rest,
		}
	}

	return { ...fromJSON(json) }
}

export const dietToJSON = (obj: Partial<IDietModel>): Partial<IDiet> => {
	const { ...rest } = obj
	return {
		...rest,
	}
}

export const DietCalories = (json: IDietCalories): IDietCaloriesModel => {
	const { id, value } = json

	return {
		id: id.toString(),
		value,
	}
}

export const dietNumberOfMealsToJSON = (model: IDietNumberOfMeals): IDietNumberOfMealsJson[] => {
	const meals: IDietNumberOfMealsJson[] = []

	Object.entries(model).forEach(([key, value]) => {
		meals.push({ meal_type: key as IDietMealType, meal_count: value.value ?? 0 })
	})

	return meals
}

export const dietFormToJSON = (model: IDietForm): IDietFormJson => {
	const {
		name,
		nameShort,
		description,
		descriptionShort,
		carbsPercent,
		proteinPercent,
		fatPercent,
		numberOfMeals,
		activeToOrderFrom,
		activeToOrderTo,
		filterTags,
		marketingTags,
		dietType,
		allergenExclusions,
		...rest
	} = model

	return {
		...rest,
		name: [generateLangContent(name)],
		name_short: [generateLangContent(nameShort)],
		description: description ? [generateLangContent(description)] : undefined,
		description_short: descriptionShort ? [generateLangContent(descriptionShort)] : undefined,
		active_to_order_from: activeToOrderFrom ? activeToOrderFrom : undefined,
		active_to_order_to: activeToOrderTo ? activeToOrderTo : undefined,
		carbs_percent: carbsPercent,
		protein_percent: proteinPercent,
		fat_percent: fatPercent,
		meal_types: dietNumberOfMealsToJSON(numberOfMeals),
		filter_tags: filterTags ? filterTags.map(f => f.value) : [],
		marketing_tags: marketingTags ? marketingTags.map(m => m.value) : [],
		allergen_exclusions: allergenExclusions ?? [],
		diet_type: dietType,
	}
}

export const DietFormToJson = (model: IDietFormUpdateModel): IDietFormUpdateJson => {
	const {
		name,
		nameShort,
		description,
		descriptionShort,
		carbsPercent,
		proteinPercent,
		fatPercent,
		numberOfMeals,
		activeToOrderFrom,
		activeToOrderTo,
		filterTags,
		marketingTags,
		dietType,
		allergenExclusions,
		dietaryTagExclusions,
		...rest
	} = model

	// TODO: Change this to a better solution
	activeToOrderTo?.setHours(23, 59, 59, 999)
	activeToOrderFrom?.setHours(23, 59, 59, 999)

	return {
		...rest,
		name: generateLangContentArray(name),
		name_short: generateLangContentArray(nameShort),
		description: description ? generateLangContentArray(description) : undefined,
		description_short: descriptionShort ? generateLangContentArray(descriptionShort) : undefined,
		diet_type: dietType,
		active_to_order_from: activeToOrderFrom ? activeToOrderFrom : undefined,
		active_to_order_to: activeToOrderTo ? activeToOrderTo : undefined,
		carbs_percent: carbsPercent,
		protein_percent: proteinPercent,
		fat_percent: fatPercent,
		meal_types: dietNumberOfMealsToJSON(numberOfMeals),
		filter_tags: filterTags ? filterTags.map(f => f.value) : [],
		marketing_tags: marketingTags ? marketingTags.map(m => m.value) : [],
		allergen_exclusions: allergenExclusions ?? [],
		dietary_tag_exclusions: dietaryTagExclusions ?? [],
	}
}

/**
 * Payloads
 */
export const DietNumberOfMealsToPayload = (model: IDietNumberOfMeals): IDietNumberOfMealsJson[] => {
	const meals: IDietNumberOfMealsJson[] = []

	Object.entries(model).forEach(([key, value]) => {
		meals.push({ meal_type: key as IDietMealType, meal_count: value.value ?? 0 })
	})

	return meals
}

export const DietSharedToPayload = (
	event: IFormSubmitEvent<IDietFormBaseModel>,
	others: IDietPayloadOthers,
): IDietSharedPayload => {
	const { allergens, dietaryTags, filterTags, marketingTags, numberOfMeals, isStatusActive, kcals } = others

	const { activeToOrderFrom, activeToOrderTo, proteinPercent, carbsPercent, fatPercent, order, isDateRange } =
		event.values

	const status = isStatusActive ? EDietStatus.ACTIVE : EDietStatus.INACTIVE

	const _activeToOrderTo = isDateRange && activeToOrderTo ? new Date(activeToOrderTo) : undefined

	let _activeToOrderFrom: Date | undefined
	if (isStatusActive && isDateRange) {
		_activeToOrderFrom = activeToOrderFrom ? new Date(activeToOrderFrom) : undefined
	} else {
		_activeToOrderFrom = undefined
	}

	if (_activeToOrderTo) _activeToOrderTo.setHours(23, 59, 59, 999)
	if (_activeToOrderFrom) _activeToOrderFrom.setHours(23, 59, 59, 999)

	return {
		active_to_order_from: _activeToOrderFrom,
		active_to_order_to: _activeToOrderTo,
		protein_percent: proteinPercent,
		carbs_percent: carbsPercent,
		fat_percent: fatPercent,
		meal_types: DietNumberOfMealsToPayload(numberOfMeals),
		filter_tags: filterTags ? filterTags.map(f => f.value) : [],
		marketing_tags: marketingTags ? marketingTags.map(m => m.value) : [],
		allergen_exclusions: allergens ? allergens : [],
		dietary_tag_exclusions: dietaryTags ? dietaryTags : [],
		status,
		order,
		kcals,
	}
}

export const DietUpdateToPayload = (
	updated: IDietModel,
	event: IFormSubmitEvent<IDietFormBaseModel>,
	others: IDietPayloadOthers,
): IDietUpdatePayload => {
	const { name, nameShort, description, descriptionShort } = event.values

	const shared = DietSharedToPayload(event, others)

	let _description: ReturnType<typeof LangContentToJson> | ReturnType<typeof generateLangContentArray> | undefined
	if (updated.description) {
		_description = LangContentToJson(updated.description, description)
	} else if (description) {
		_description = generateLangContentArray(description)
	} else {
		_description = undefined
	}

	let _descriptionShort:
		| ReturnType<typeof LangContentToJson>
		| ReturnType<typeof generateLangContentArray>
		| undefined
	if (updated.descriptionShort) {
		_descriptionShort = LangContentToJson(updated.descriptionShort, descriptionShort)
	} else if (descriptionShort) {
		_descriptionShort = generateLangContentArray(descriptionShort)
	} else {
		_descriptionShort = undefined
	}

	return {
		id: updated.id,
		name: LangContentToJson(updated.name, name),
		name_short: LangContentToJson(updated.nameShort, nameShort),
		description: _description,
		description_short: _descriptionShort,
		diet_type: updated.dietType,
		...shared,
	}
}

export const DietCreateToPayload = (
	event: IFormSubmitEvent<IDietFormBaseModel>,
	others: IDietPayloadOthers,
): IDietCreatePayload => {
	const { name, nameShort, description, descriptionShort } = event.values

	const shared = DietSharedToPayload(event, others)

	const _description = description ? generateLangContentArray(description) : undefined
	const _descriptionShort = descriptionShort ? generateLangContentArray(descriptionShort) : undefined

	return {
		name: generateLangContentArray(name),
		name_short: generateLangContentArray(nameShort),
		description: _description,
		description_short: _descriptionShort,
		diet_type: EDietType.STANDARD,
		...shared,
	}
}
